import React from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import moment from 'moment';

import { Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';
import { getCryptoDecimals } from 'src/core/const';
import { setBreadcrumbs } from 'src/ui';
import { getToken } from 'src/auth';
import { getUser } from 'src/user';
import { getFIs, fetchFIs } from 'src/financial-institution';
import { Select, MenuItem, FormControl, InputLabel, Button, Box, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from 'src/api/config';
import {
  fetchTransactionSummary,
  fetchTransactionList,
  fetchFITransactionSummary,
  fetchFITransactionList,
  fetchAllTransactionsSummary,
  fetchAllTransactionsList,
} from 'src/transaction';

import QuickDateRangeSelector, { DATE_RANGES } from '../components/QuickDateRangeSelector';
import TransactionFiltersDropdown from '../components/TransactionFiltersDropdown';

import './TransactionsListPage.css';

const SummaryTab = ({ data, loading, error, dateRange, onStatusClick }) => {
  if (loading) {
    return <div className="tab-content">Loading summary...</div>;
  }

  if (error) {
    return <div className="tab-content error">Error: {error}</div>;
  }

  if (!data) {
    return <div className="tab-content">No summary data available</div>;
  }

  return (
    <div className="tab-content summary-section">
      <div className="summary-grid">
        <div 
          className="summary-card clickable" 
          onClick={() => onStatusClick('paid')}
          role="button"
          tabIndex={0}
        >
          <div className="summary-label">Paid Transactions</div>
          <div className="summary-value">
            {(data.paid_transactions || 0).toLocaleString()}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-label">Total USD Volume</div>
          <div className="summary-value">
            ${parseFloat(data.total_usd_volume || data.total_amount_paid || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-label">Total Fees Collected</div>
          <div className="summary-value">
            ${parseFloat(data.total_fees_collected || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
          </div>
        </div>

        {data.total_commission !== undefined && (
          <>
            <div className="summary-card" style={{ backgroundColor: '#e8f5e9', borderLeft: '4px solid #2e7d32' }}>
              <div className="summary-label" style={{ color: '#2e7d32', fontWeight: 600 }}>Total Commission</div>
              <div className="summary-value" style={{ color: '#2e7d32' }}>
                ${parseFloat(data.total_commission || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
              </div>
            </div>

            {data.accounts_with_payments !== undefined && (
              <div className="summary-card">
                <div className="summary-label">Active Accounts</div>
                <div className="summary-value">
                  {data.accounts_with_payments}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

SummaryTab.propTypes = {
  data: propTypes.object,
  loading: propTypes.bool,
  error: propTypes.string,
  dateRange: propTypes.array.isRequired,
  onStatusClick: propTypes.func.isRequired,
};

const TransactionDetailModal = ({ transaction, onClose, token, user }) => {
  if (!transaction) return null;

  // Use default FI settings for commission calculation
  // These are typical defaults - actual FI settings would need to be fetched separately
  const calculateCommission = () => {
    const resellerSplitPercent = 60; // Default split
    const buyRatePercent = 1; // Default buy rate


    
    // Total fees collected from merchant
    const merchantFee = parseFloat(transaction.merchant_fee_usd || 0);
    const platformFee = parseFloat(transaction.platform_fee_usd || 0);
    const totalFeesCollected = merchantFee + platformFee;
    
    // Calculate hard costs (exchange fee, bank fee, buffer)
    const exchangeFee = parseFloat(transaction.fee_components?.exchange_fee || transaction.exchange_fee_usd || 0);
    const bankFee = parseFloat(transaction.fee_components?.bank_fee || transaction.bank_fee_usd || 0);
    const bufferFee = parseFloat(transaction.fee_components?.buffer || 0);
    const totalHardCosts = exchangeFee + bankFee + bufferFee;
    
    // Calculate net revenue (after hard costs)
    const netRevenue = totalFeesCollected - totalHardCosts;
    
    // Calculate split
    const fiShare = netRevenue * (resellerSplitPercent / 100);
    const solventShare = netRevenue * ((100 - resellerSplitPercent) / 100);
    
    // Calculate buy rate minimum
    const transactionAmount = parseFloat(transaction.order_amount_usd || 0);
    const buyRateMinimum = transactionAmount * (buyRatePercent / 100);
    
    // FI gets the greater of revenue split or buy rate minimum
    const fiCommission = Math.max(fiShare, buyRateMinimum);
    const solventCommission = netRevenue - fiCommission;

    return {
      totalFeesCollected,
      netRevenue,
      exchangeFee,
      bankFee,
      bufferFee,
      totalHardCosts,
      fiShare,
      solventShare,
      buyRateMinimum,
      fiCommission,
      solventCommission,
      resellerSplitPercent,
      buyRatePercent,
    };
  };

  const commission = calculateCommission();

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content transaction-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Transaction Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="detail-section">
            <h3>Payment Session</h3>
            <div className="detail-row">
              <span className="detail-label">Session ID:</span>
              <span className="detail-value">{transaction.session_id}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Status:</span>
              <span className={`status status-${transaction.status}`}>
                {transaction.status_display || transaction.status}
              </span>
            </div>
            {user && user.isSuperAdmin && (
              <>
                <div className="detail-row">
                  <span className="detail-label">Internal ID:</span>
                  <span className="detail-value">{transaction.id}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Detection Source:</span>
                  <span className="detail-value">{transaction.detection_source || 'N/A'}</span>
                </div>
              </>
            )}
            <div className="detail-row">
              <span className="detail-label">Amount (USD):</span>
              <span className="detail-value">${parseFloat(transaction.order_amount_usd || 0).toFixed(2)}</span>
            </div>
            {user && user.isSuperAdmin && (
              <div className="detail-row">
                <span className="detail-label">Amount ({transaction.crypto_currency || 'Crypto'}):</span>
                <span className="detail-value">
                  {parseFloat(transaction.expected_crypto || 0).toFixed(getCryptoDecimals(transaction.crypto_currency))} {transaction.crypto_currency || ''}
                </span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Merchant Fee:</span>
              <span className="detail-value">${parseFloat(transaction.merchant_fee_usd || 0).toFixed(2)}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Platform Fee:</span>
              <span className="detail-value">${parseFloat(transaction.platform_fee_usd || 0).toFixed(2)}</span>
            </div>
            {user && user.isSuperAdmin && (
              <>
                <div className="detail-row">
                  <span className="detail-label">Exchange Fee (0.25%):</span>
                  <span className="detail-value">${parseFloat(transaction.fee_components?.exchange_fee || transaction.exchange_fee_usd || 0).toFixed(6)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Bank Fee (0.25%):</span>
                  <span className="detail-value">${parseFloat(transaction.fee_components?.bank_fee || transaction.bank_fee_usd || 0).toFixed(6)}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Buffer Fee:</span>
                  <span className="detail-value">${parseFloat(transaction.fee_components?.buffer || 0).toFixed(6)}</span>
                </div>
              </>
            )}
            <div className="detail-row">
              <span className="detail-label">Total Amount:</span>
              <span className="detail-value total-amount">${parseFloat(transaction.total_amount || 0).toFixed(2)}</span>
            </div>
          </div>

          <div className="detail-section">
            <h3>Merchant Information</h3>
            <div className="detail-row">
              <span className="detail-label">Merchant Name:</span>
              <span className="detail-value">{transaction.businessName || transaction.merchant_name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Merchant ID:</span>
              <span className="detail-value">{transaction.merchant || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Terminal Name:</span>
              <span className="detail-value">{transaction.terminal_name || 'N/A'}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Terminal ID:</span>
              <span className="detail-value">{transaction.terminal || 'N/A'}</span>
            </div>
          </div>

          {commission && (user && (user.isSuperAdmin || user.isBankAdmin || user.isPrimaryBankAdmin)) && (
            <div className="detail-section commission-section">
              <h3>Commission Split</h3>
              <>
                  <div className="detail-row">
                    <span className="detail-label">Total Fees Collected:</span>
                    <span className="detail-value">${commission.totalFeesCollected.toFixed(2)}</span>
                  </div>
                  
                  {user && user.isSuperAdmin && (
                    <>
                      <div className="detail-row">
                        <span className="detail-label">Hard Costs (Exchange + Bank + Buffer):</span>
                        <span className="detail-value">-${commission.totalHardCosts.toFixed(6)}</span>
                      </div>
                      <div className="detail-row" style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
                        <span className="detail-label">Exchange Fee:</span>
                        <span className="detail-value">${commission.exchangeFee.toFixed(6)}</span>
                      </div>
                      <div className="detail-row" style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
                        <span className="detail-label">Bank Fee:</span>
                        <span className="detail-value">${commission.bankFee.toFixed(6)}</span>
                      </div>
                      <div className="detail-row" style={{ paddingLeft: '20px', fontSize: '12px', color: '#666' }}>
                        <span className="detail-label">Buffer:</span>
                        <span className="detail-value">${commission.bufferFee.toFixed(6)}</span>
                      </div>
                      
                      <div className="detail-row" style={{ fontWeight: 600, borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                        <span className="detail-label">Net Revenue:</span>
                        <span className="detail-value">${commission.netRevenue.toFixed(2)}</span>
                      </div>
                      
                      <div style={{ height: '10px' }}></div>
                      
                      <div className="detail-row" style={{ background: '#e8f5e9', padding: '10px', borderRadius: '4px' }}>
                        <span className="detail-label">FI Commission ({commission.resellerSplitPercent}% split):</span>
                        <span className="detail-value" style={{ color: '#2e7d32', fontWeight: 600 }}>
                          ${commission.fiShare.toFixed(4)}
                        </span>
                      </div>
                      
                      <div className="detail-row" style={{ background: '#fff3e0', padding: '10px', borderRadius: '4px' }}>
                        <span className="detail-label">Buy Rate Minimum ({commission.buyRatePercent}%):</span>
                        <span className="detail-value" style={{ color: '#f57c00', fontWeight: 600 }}>
                          ${commission.buyRateMinimum.toFixed(4)}
                        </span>
                      </div>
                      
                      <div style={{ height: '10px' }}></div>
                    </>
                  )}
                  
                  <div className="detail-row" style={{ background: '#e8f5e9', padding: '12px', borderRadius: '4px', borderLeft: '4px solid #2e7d32' }}>
                    <span className="detail-label" style={{ fontSize: '15px', fontWeight: 700 }}>
                      {user && user.isSuperAdmin ? 'FI Gets (Greater of Above):' : (user && (user.isBankAdmin || user.isPrimaryBankAdmin)) ? 'Commission Earned:' : 'Commission Earned:'}
                    </span>
                    <span className="detail-value" style={{ color: '#2e7d32', fontSize: '18px', fontWeight: 700 }}>
                      ${commission.fiCommission.toFixed(4)}
                    </span>
                  </div>
                  
                  {user && user.isSuperAdmin && (
                    <div className="detail-row" style={{ background: '#e3f2fd', padding: '12px', borderRadius: '4px', borderLeft: '4px solid #1976d2' }}>
                      <span className="detail-label" style={{ fontSize: '15px', fontWeight: 700 }}>
                        Solvent Gets:
                      </span>
                      <span className="detail-value" style={{ color: '#1976d2', fontSize: '18px', fontWeight: 700 }}>
                        ${commission.solventCommission.toFixed(4)}
                      </span>
                    </div>
                  )}
                </>
            </div>
          )}

          {user && user.isSuperAdmin && (
            <div className="detail-section">
              <h3>Blockchain Information</h3>
              <div className="detail-row">
                <span className="detail-label">Terminal Address:</span>
                <span className="detail-value monospace">{transaction.terminal_address || 'N/A'}</span>
              </div>
              {transaction.received_tx_hash && (
                <div className="detail-row">
                  <span className="detail-label">Transaction Hash:</span>
                  <span className="detail-value monospace">
                    <a 
                      href={`https://solscan.io/tx/${transaction.received_tx_hash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="tx-link"
                    >
                      {transaction.received_tx_hash}
                    </a>
                  </span>
                </div>
              )}
              {transaction.received_crypto && (
                <div className="detail-row">
                  <span className="detail-label">Confirmed Amount:</span>
                  <span className="detail-value">
                    {parseFloat(transaction.received_crypto || 0).toFixed(getCryptoDecimals(transaction.crypto_currency))} {transaction.crypto_currency || ''}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="detail-section">
            <h3>Timestamps</h3>
            <div className="detail-row">
              <span className="detail-label">Created:</span>
              <span className="detail-value">{moment(transaction.created_at).format('MMMM DD, YYYY HH:mm:ss z')}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Start Time:</span>
              <span className="detail-value">{moment(transaction.start_time).format('MMMM DD, YYYY HH:mm:ss z')}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Timeout:</span>
              <span className="detail-value">{moment(transaction.timeout_at).format('MMMM DD, YYYY HH:mm:ss z')}</span>
            </div>
            {transaction.received_at && (
              <div className="detail-row">
                <span className="detail-label">Paid At:</span>
                <span className="detail-value">{moment(transaction.received_at).format('MMMM DD, YYYY HH:mm:ss z')}</span>
              </div>
            )}
            <div className="detail-row">
              <span className="detail-label">Time Remaining:</span>
              <span className="detail-value">{transaction.time_remaining_seconds}s</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Expired:</span>
              <span className="detail-value">{transaction.is_expired ? 'Yes' : 'No'}</span>
            </div>
          </div>

          {transaction.external_reference && (
            <div className="detail-section">
              <h3>Additional Information</h3>
              <div className="detail-row">
                <span className="detail-label">External Reference:</span>
                <span className="detail-value">{transaction.external_reference}</span>
              </div>
              {transaction.metadata && Object.keys(transaction.metadata).length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">Metadata:</span>
                  <span className="detail-value">
                    <pre className="metadata-pre">{JSON.stringify(transaction.metadata, null, 2)}</pre>
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

TransactionDetailModal.propTypes = {
  transaction: propTypes.object,
  onClose: propTypes.func.isRequired,
  token: propTypes.object.isRequired,
  user: propTypes.object,
};

const TransactionsTab = ({ data, loading, error, onPageChange, currentPage, statusFilter, onSearch, token, user }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const [minAmount, setMinAmount] = React.useState('');
  const [maxAmount, setMaxAmount] = React.useState('');
  const [selectedTransaction, setSelectedTransaction] = React.useState(null);

  // Memoize the close handler to prevent re-renders
  const handleCloseModal = React.useCallback(() => {
    setSelectedTransaction(null);
  }, []);

  if (loading) {
    return <div className="tab-content">Loading transactions...</div>;
  }

  if (error) {
    return <div className="tab-content error">Error: {error}</div>;
  }

  if (!data || !data.results || data.results.length === 0) {
    return <div className="tab-content">No transactions found</div>;
  }

  const handleSearch = () => {
    const filters = {};
    if (searchTerm) filters.search = searchTerm;
    if (minAmount) filters.min_amount = minAmount;
    if (maxAmount) filters.max_amount = maxAmount;
    onSearch(filters);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setMinAmount('');
    setMaxAmount('');
    onSearch({});
  };

  return (
    <div className="tab-content">
      <div className="transactions-table-container">
        <table className="simple-transactions-table">
          <thead>
            <tr>
              <th className="col-session-id">Session ID</th>
              <th className="col-merchant">Merchant</th>
              <th className="col-amount">Amount</th>
              <th className="col-status">Status</th>
              <th className="col-created">Created</th>
            </tr>
          </thead>
          <tbody>
            {data.results.map((transaction) => (
              <tr 
                key={transaction.id} 
                className="clickable-row"
                onClick={() => setSelectedTransaction(transaction)}
                style={{ cursor: 'pointer' }}
              >
                <td className="col-session-id" title={transaction.session_id}>
                  {transaction.session_id}
                </td>
                <td className="col-merchant" title={transaction.businessName || transaction.merchant_name || '-'}>
                  {transaction.businessName || transaction.merchant_name || '-'}
                </td>
                <td className="col-amount">
                  ${parseFloat(transaction.order_amount_usd || 0).toFixed(2)}
                </td>
                <td className="col-status">
                  <span className={`status status-${transaction.status}`}>
                    {transaction.status_display || transaction.status}
                  </span>
                </td>
                <td className="col-created">
                  {moment(transaction.created_at).format('MM/DD/YY HH:mm z')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {data.count > data.results.length && (
        <div className="pagination-controls">
          <div className="pagination-info">
            Showing {data.results.length} of {data.count} transactions
          </div>
          <div className="pagination-buttons">
            {data.previous && (
              <button
                className="pagination-button"
                onClick={() => onPageChange(currentPage - 1)}
              >
                ← Previous
              </button>
            )}
            {data.next && (
              <button
                className="pagination-button"
                onClick={() => onPageChange(currentPage + 1)}
              >
                Next →
              </button>
            )}
          </div>
        </div>
      )}

      {selectedTransaction && (
        <TransactionDetailModal
          transaction={selectedTransaction}
          onClose={handleCloseModal}
          token={token}
          user={user}
        />
      )}
    </div>
  );
};

TransactionsTab.propTypes = {
  data: propTypes.object,
  loading: propTypes.bool,
  error: propTypes.string,
  onPageChange: propTypes.func.isRequired,
  currentPage: propTypes.number.isRequired,
  statusFilter: propTypes.string,
  onSearch: propTypes.func.isRequired,
  token: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
};

const TransactionsListPage = ({
  setBreadcrumbs,
  token,
  user,
  financialInstitutions,
  fetchFIs,
  summaryData,
  summaryLoading,
  summaryError,
  listData,
  listLoading,
  listError,
  fetchTransactionSummary,
  fetchTransactionList,
  fetchFITransactionSummary,
  fetchFITransactionList,
  fetchAllTransactionsSummary,
  fetchAllTransactionsList,
  externalDateRange, // Optional: date range from parent component (dashboard)
  hideDatePicker, // Optional: hide date picker when embedded in dashboard
}) => {
  const [searchParams] = useSearchParams();
  const urlFiId = searchParams.get('fi_id');
  const urlAccountId = searchParams.get('account_id');
  
  const [currentPage, setCurrentPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState(null);
  const [searchFilters, setSearchFilters] = React.useState({});
  
  // For FI Admins, pre-select their FI (they only have access to one FI)
  // For Super Admins, default to empty (All FIs)
  const initialFiId = urlFiId || (user.isBankAdmin && user.financialInstitutionId ? String(user.financialInstitutionId) : '');
  
  const [filterFiId, setFilterFiId] = React.useState(initialFiId);
  const [filterAccountId, setFilterAccountId] = React.useState(urlAccountId || '');
  const [merchantAccounts, setMerchantAccounts] = React.useState([]);
  const [loadingMerchants, setLoadingMerchants] = React.useState(false);
  
  // Filter UI state
  const [dateRangeMode, setDateRangeMode] = React.useState(DATE_RANGES.MTD);
  const [filtersAnchorEl, setFiltersAnchorEl] = React.useState(null);
  const [showFilters, setShowFilters] = React.useState(false);
  const [activeFilters, setActiveFilters] = React.useState({
    searchSessionId: '',
    searchTxHash: '',
    fiId: initialFiId,
    merchantId: urlAccountId || '',
    status: ['paid', 'underfunded', 'overfunded', 'partial_accepted'], // Default to actionable statuses
    dateFrom: null,
    dateTo: null,
  });
  
  // Pending filters state for UI inputs (to prevent auto-refresh)
  const [pendingFilters, setPendingFilters] = React.useState(activeFilters);

  // Sync pending filters when active filters change (e.g. from URL)
  React.useEffect(() => {
    setPendingFilters(activeFilters);
  }, [activeFilters]);
  
  // Helper: Calculate date range based on mode
  const calculateDateRange = (mode) => {
    const now = new Date();
    const today = moment(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
    
    switch (mode) {
      case DATE_RANGES.TODAY:
        return [
          today.toDate(),
          today.clone().add(1, 'days').subtract(1, 'seconds').toDate(),
        ];
      case DATE_RANGES.YESTERDAY:
        return [
          today.clone().subtract(1, 'days').toDate(),
          today.clone().subtract(1, 'seconds').toDate(),
        ];
      case DATE_RANGES.THIS_WEEK:
        return [
          today.clone().startOf('week').toDate(),
          today.clone().add(1, 'days').subtract(1, 'seconds').toDate(),
        ];
      case DATE_RANGES.MTD:
        return [
          today.clone().startOf('month').toDate(),
          today.clone().add(1, 'days').subtract(1, 'seconds').toDate(),
        ];
      case DATE_RANGES.CUSTOM:
        // Use custom dates from activeFilters or default to last 14 days
        return [
          activeFilters.dateFrom || today.clone().subtract(14, 'days').toDate(),
          activeFilters.dateTo || today.clone().add(1, 'days').subtract(1, 'seconds').toDate(),
        ];
      default:
        return [
          today.clone().subtract(14, 'days').toDate(),
          today.clone().add(1, 'days').subtract(1, 'seconds').toDate(),
        ];
    }
  };
  
  // Fetch FIs on mount to ensure fresh data (only for super admins)
  React.useEffect(() => {
    if (user && user.isSuperAdmin) {
      fetchFIs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.isSuperAdmin]);
  
  // Use external date range if provided (from dashboard), otherwise calculate from mode
  const internalDateRange = React.useMemo(() => calculateDateRange(dateRangeMode), [dateRangeMode, activeFilters.dateFrom, activeFilters.dateTo]);
  
  // Use external date range if provided, otherwise use internal
  const dateRange = externalDateRange || internalDateRange;

  React.useEffect(() => {
    const breadcrumbs = [{ label: 'Reports', href: '/reports' }];
    
    // Add breadcrumb context when filtering by URL params
    if (urlFiId) {
      // Try to get FI name from financialInstitutions list
      const fi = financialInstitutions?.items?.find(f => f.id === parseInt(urlFiId));
      const fiName = fi?.businessName || `Financial Institution #${urlFiId}`;
      
      if (urlAccountId) {
        breadcrumbs.push({ label: fiName, href: `/reports?fi_id=${urlFiId}` });
        breadcrumbs.push({ label: `Account ${urlAccountId}` });
      } else {
        breadcrumbs.push({ label: fiName });
      }
    }
    
    setBreadcrumbs(breadcrumbs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlFiId, urlAccountId]); // Only depend on URL params, not financialInstitutions

  React.useEffect(() => {
    if (!token || !token.isValid || !user || !user.isValid) {
      console.log('TransactionsListPage: token or user not ready', { token: !!token, user: !!user });
      return;
    }

    if (!token.isValid() || !user.isValid()) {
      console.log('TransactionsListPage: token or user invalid');
      return;
    }

    // Convert local date range to UTC for API query
    const dateFrom = moment(dateRange[0]).toISOString();
    const dateTo = moment(dateRange[1]).toISOString();

    // Use activeFilters state (which includes URL params) or fall back to user's FI/account
    const effectiveFiId = activeFilters.fiId || user.financialInstitutionId;
    const effectiveAccountId = activeFilters.merchantId || user.accountId;

    console.log('TransactionsListPage fetching data:', {
      dateFrom,
      dateTo,
      dateRangeTimestamps: [dateRange[0]?.getTime(), dateRange[1]?.getTime()],
      urlFiId,
      urlAccountId,
      effectiveFiId,
      effectiveAccountId,
      currentPage,
      statusFilter: activeFilters.status,
      searchFilters: {
        session_id: activeFilters.searchSessionId,
        tx_hash: activeFilters.searchTxHash,
      },
      userFiId: user.financialInstitutionId,
      userAccountId: user.accountId,
      isSuperAdmin: user.isSuperAdmin,
      dateRangeMode,
    });

    // Log Redux state BEFORE fetch
    console.log('Redux state BEFORE fetch:', {
      hasListData: !!listData,
      listLoading,
      listError,
      resultCount: listData?.results?.length,
      firstResultDate: listData?.results?.[0]?.created_at,
      lastResultDate: listData?.results?.[listData?.results?.length - 1]?.created_at,
    });
    
    // For non-super-admins, require an FI ID
    if (!effectiveFiId && !user.isSuperAdmin) {
      console.log('TransactionsListPage: No FI ID available, skipping fetch');
      return;
    }

    // Determine if user should see FI-wide data or account-specific data
    // If URL params specify an account, show account-specific data
    const isFIAdmin = user.isSuperAdmin || user.isBankAdmin || user.isPrimaryBankAdmin;
    const showAccountSpecific = urlAccountId || !isFIAdmin;

    // Always fetch summary
    if (user.isSuperAdmin && !effectiveFiId) {
      // Super admin viewing ALL FIs - fetch all transactions
      fetchAllTransactionsSummary({
        dateFrom,
        dateTo,
        status: activeFilters.status,
      });
    } else if (showAccountSpecific && effectiveAccountId) {
      // Account-specific summary
      fetchTransactionSummary({
        fi: effectiveFiId,
        account: effectiveAccountId,
        dateFrom,
        dateTo,
        status: activeFilters.status,
      });
    } else if (effectiveFiId) {
      // FI-wide summary (all accounts under the FI)
      fetchFITransactionSummary({
        fi: effectiveFiId,
        dateFrom,
        dateTo,
        status: activeFilters.status,
      });
    }

    // Always fetch transaction list
    const searchParams = {};
    // Map session ID to 'search' param as the action expects 'search'
    if (activeFilters.searchSessionId) searchParams.search = activeFilters.searchSessionId;
    if (activeFilters.searchTxHash) searchParams.tx_hash = activeFilters.searchTxHash;
    
    const statusParam = Array.isArray(activeFilters.status) 
      ? activeFilters.status.join(',') 
      : activeFilters.status;
    
    if (user.isSuperAdmin && !effectiveFiId) {
      // Super admin viewing ALL FIs - fetch all transactions
      console.log('Calling fetchAllTransactionsList with:', {
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusParam,
        ...searchParams,
      });
      fetchAllTransactionsList({
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusParam,
        ...searchParams,
      });
    } else if (showAccountSpecific && effectiveAccountId) {
      // Account-specific transaction list
      fetchTransactionList({
        fi: effectiveFiId,
        account: effectiveAccountId,
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusParam,
        ...searchParams,
      });
    } else if (effectiveFiId) {
      // FI-wide transaction list (all accounts under the FI)
      console.log('Calling fetchFITransactionList with:', {
        fi: effectiveFiId,
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusParam,
        ...searchParams,
      });
      fetchFITransactionList({
        fi: effectiveFiId,
        page: currentPage,
        pageSize: 50,
        dateFrom,
        dateTo,
        status: statusParam,
        ...searchParams,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRange[0]?.getTime(),
    dateRange[1]?.getTime(),
    currentPage,
    activeFilters.status, // This might need deep comparison if it's an array, but React might handle it if reference changes
    activeFilters.searchSessionId,
    activeFilters.searchTxHash,
    activeFilters.fiId,
    activeFilters.merchantId,
    user.financialInstitutionId,
    user.accountId,
    user.isSuperAdmin,
    user.isBankAdmin,
    user.isPrimaryBankAdmin,
    dateRangeMode,
  ]);

  const handleStatusClick = (status) => {
    const newFilters = { ...activeFilters, status: [status] };
    handleApplyFilters(newFilters);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleDateRangeChange = (start, end) => {
    console.log('Date range changed:', { start, end, startTime: start?.getTime(), endTime: end?.getTime() });
    setDateRange([start, end]);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (e) => {
    const newStatus = e.target.value === 'all' ? null : e.target.value;
    setStatusFilter(newStatus);
    setCurrentPage(1);
  };

  const handleSearch = (filters) => {
    setSearchFilters(filters);
    setCurrentPage(1);
  };

  const handleFiFilterChange = async (event) => {
    const newFiId = event.target.value;
    setFilterFiId(newFiId);
    setFilterAccountId(''); // Clear account filter when FI changes
    setCurrentPage(1);
    
    // Fetch merchants for this FI
    if (newFiId && token && token.accessToken) {
      setLoadingMerchants(true);
      try {
        console.log('Fetching merchants for FI:', newFiId);
        const response = await fetch(`${api.hostname}/api/v1/transactions/fi-merchants/${newFiId}/`, {
          headers: {
            'Authorization': `Bearer ${token.accessToken}`,
          },
        });
        console.log('Merchant fetch response status:', response.status);
        if (response.ok) {
          const data = await response.json();
          console.log('Merchant data received:', data);
          console.log('Number of merchants:', Array.isArray(data) ? data.length : 'not an array');
          setMerchantAccounts(data || []);
        } else {
          const errorText = await response.text();
          console.error('Failed to fetch merchants - status:', response.status, 'error:', errorText);
        }
      } catch (error) {
        console.error('Failed to fetch merchants:', error);
      } finally {
        setLoadingMerchants(false);
      }
    } else {
      setMerchantAccounts([]);
    }
  };

  const handleAccountFilterChange = (event) => {
    setFilterAccountId(event.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilterFiId(urlFiId || '');
    setFilterAccountId(urlAccountId || '');
    setStatusFilter(null);
    setSearchFilters({});
    setCurrentPage(1);
  };

  // Load merchants when FI filter changes
  React.useEffect(() => {
    const fiIdToLoad = pendingFilters.fiId;
    
    console.log('FI filter changed - fiIdToLoad:', fiIdToLoad, 'token:', !!token, 'accessToken:', !!token?.accessToken);
    
    if (fiIdToLoad && token && token.accessToken) {
      setLoadingMerchants(true);
      console.log('Fetching merchants for FI:', fiIdToLoad);
      fetch(`${api.hostname}/api/v1/transactions/fi-merchants/${fiIdToLoad}/`, {
        headers: { 'Authorization': `Bearer ${token.accessToken}` },
      })
        .then(res => {
          console.log('Fetch response status:', res.status);
          return res.ok ? res.json() : Promise.reject('Response not OK');
        })
        .then(data => {
          console.log('Loaded merchants:', data);
          console.log('Number of merchants loaded:', Array.isArray(data) ? data.length : 'not an array');
          setMerchantAccounts(data || []);
        })
        .catch(err => console.error('Failed to fetch merchants:', err))
        .finally(() => setLoadingMerchants(false));
    } else {
      setMerchantAccounts([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFilters.fiId, token?.accessToken]);

  const getDateRangeLabel = () => {
    const start = dateRange[0];
    const end = dateRange[1];
    
    switch (dateRangeMode) {
      case DATE_RANGES.TODAY:
        return 'Today';
      case DATE_RANGES.YESTERDAY:
        return 'Yesterday';
      case DATE_RANGES.THIS_WEEK:
        return 'This Week';
      case DATE_RANGES.MTD:
        return 'Month to Date';
      case DATE_RANGES.CUSTOM:
        return `${moment(start).format('MM/DD/YYYY')} - ${moment(end).format('MM/DD/YYYY')}`;
      default:
        return 'Transaction Reports';
    }
  };

  // Handler: Date range mode change
  const handleDateRangeModeChange = (newMode) => {
    setDateRangeMode(newMode);
    setCurrentPage(1);
    
    // If custom mode, open filters dialog
    if (newMode === DATE_RANGES.CUSTOM) {
      const trigger = typeof document !== 'undefined' ? document.getElementById('filters-trigger-button') : null;
      if (trigger) {
        setFiltersAnchorEl(trigger);
      }
    }
  };
  
  // Handler: Apply filters from dialog
  const handleApplyFilters = (newFilters) => {
    setActiveFilters(newFilters);
    
    // Update legacy state for backward compatibility
    setFilterFiId(newFilters.fiId);
    setFilterAccountId(newFilters.merchantId);
    setStatusFilter(newFilters.status || null);
    
    // Set search filters
    const searchObj = {};
    if (newFilters.searchSessionId) searchObj.session_id = newFilters.searchSessionId;
    if (newFilters.searchTxHash) searchObj.tx_hash = newFilters.searchTxHash;
    setSearchFilters(searchObj);
    
    setCurrentPage(1);
  };
  
  // Handler: Remove individual filter
  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...activeFilters };
    
    switch (filterKey) {
      case 'dateRange':
        setDateRangeMode(DATE_RANGES.MTD);
        newFilters.dateFrom = null;
        newFilters.dateTo = null;
        break;
      case 'searchSessionId':
        newFilters.searchSessionId = '';
        break;
      case 'searchTxHash':
        newFilters.searchTxHash = '';
        break;
      case 'fiId':
        newFilters.fiId = '';
        break;
      case 'merchantId':
        newFilters.merchantId = '';
        break;
      case 'status':
        newFilters.status = ['paid', 'underfunded', 'overfunded', 'partial_accepted'];
        break;
      default:
        break;
    }
    
    handleApplyFilters(newFilters);
  };
  
  // Handler: Clear all filters
  const handleClearAllFilters = () => {
    setDateRangeMode(DATE_RANGES.MTD);
    const resetFilters = {
      searchSessionId: '',
      searchTxHash: '',
      fiId: urlFiId || initialFiId,
      merchantId: urlAccountId || '',
      status: ['paid', 'underfunded', 'overfunded', 'partial_accepted'],
      dateFrom: null,
      dateTo: null,
    };
    handleApplyFilters(resetFilters);
  };
  
  // Count active filters (excluding default FI/merchant from URL)
  const getActiveFilterCount = () => {
    let count = 0;
    if (dateRangeMode !== DATE_RANGES.MTD) count++;
    if (activeFilters.searchSessionId) count++;
    if (activeFilters.searchTxHash) count++;
    if (activeFilters.fiId && activeFilters.fiId !== initialFiId) count++;
    if (activeFilters.merchantId && activeFilters.merchantId !== (urlAccountId || '')) count++;
    if (activeFilters.status) count++;
    return count;
  };
  
  const handleOpenFilters = () => {
    setShowFilters(prev => !prev);
  };

  const handleCloseFilters = () => {
    setFiltersAnchorEl(null);
  };
  
  if (!user.isValid()) {
    return (
      <div className="transactions-no-access-container">
        <MuiTitle tag="h1" isSize={1} hasTextColor="info">
          Loading...
        </MuiTitle>
      </div>
    );
  }

  return (
    <div className="transactions-list-page">
      {/* Header Row */}
      <div className="page-header-flex">
         <div className="header-title">
            <MuiTitle tag="h2" isSize={5} hasTextColor="info">
              {getDateRangeLabel()}
            </MuiTitle>
         </div>
         <div className="header-actions">
            {!hideDatePicker && (
                <QuickDateRangeSelector
                  value={dateRangeMode}
                  onChange={handleDateRangeModeChange}
                />
            )}
         </div>
      </div>

      {/* Transaction Filters Dropdown */}
      <TransactionFiltersDropdown
        anchorEl={filtersAnchorEl}
        onClose={handleCloseFilters}
        onApply={handleApplyFilters}
        filters={activeFilters}
        userRole={user.isSuperAdmin ? 'SuperAdmin' : user.isBankAdmin ? 'FI Admin' : 'Merchant'}
        financialInstitutions={financialInstitutions?.items || []}
        merchants={merchantAccounts}
        showCustomDatePicker={dateRangeMode === DATE_RANGES.CUSTOM}
      />

      <Columns>
        <Column>
          <div className="tab-container">
            {/* Summary Section */}
            <SummaryTab
                data={summaryData}
                loading={summaryLoading}
                error={summaryError}
                dateRange={dateRange}
                onStatusClick={handleStatusClick}
            />

            {/* Filters Header */}
            <div className="filters-section-header">
                <h3>Transactions</h3>
                <div className="filters-toggle-wrapper">
                    <button
                      type="button"
                      className="btn-filters-toggle"
                      onClick={handleOpenFilters}
                      title="Toggle Filters"
                    >
                      <FontAwesomeIcon icon={['fal', 'sliders-h']} />
                    </button>
                </div>
            </div>

            {showFilters && (
              <div className="filters-bar condensed">
                <div className="filters-grid">
                  {!hideDatePicker && (
                    <>
                      <div className="filter-group">
                        <label className="filter-label">Start Date</label>
                        <TextField
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={pendingFilters.dateFrom ? moment(pendingFilters.dateFrom).format('YYYY-MM-DD') : ''}
                          onChange={(e) =>
                            setPendingFilters({
                              ...pendingFilters,
                              dateFrom: e.target.value ? moment(e.target.value).startOf('day').toDate() : null,
                            })
                          }
                        />
                      </div>

                      <div className="filter-group">
                        <label className="filter-label">End Date</label>
                        <TextField
                          type="date"
                          size="small"
                          InputLabelProps={{ shrink: true }}
                          value={pendingFilters.dateTo ? moment(pendingFilters.dateTo).format('YYYY-MM-DD') : ''}
                          onChange={(e) =>
                            setPendingFilters({
                              ...pendingFilters,
                              dateTo: e.target.value ? moment(e.target.value).endOf('day').toDate() : null,
                            })
                          }
                        />
                      </div>
                    </>
                  )}

                  {/* Session ID Search */}
                  <div className="filter-group">
                    <label className="filter-label">Session ID</label>
                    <TextField
                      size="small"
                      placeholder="Search Session ID"
                      value={pendingFilters.searchSessionId || ''}
                      onChange={(e) =>
                        setPendingFilters({
                          ...pendingFilters,
                          searchSessionId: e.target.value,
                        })
                      }
                    />
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Transaction Status</label>
                    <div className="filter-checkboxes">
                      {['paid', 'refunded', 'pending', 'expired', 'failed'].map(status => (
                        <label className="filter-checkbox" key={status}>
                          <input
                            type="checkbox"
                            checked={Array.isArray(pendingFilters.status) ? pendingFilters.status.includes(status) : pendingFilters.status === status}
                            onChange={(e) => {
                              const currentStatuses = Array.isArray(pendingFilters.status) ? pendingFilters.status : (pendingFilters.status ? [pendingFilters.status] : []);
                              const newStatuses = e.target.checked
                                ? [...currentStatuses, status]
                                : currentStatuses.filter(s => s !== status);
                              
                              setPendingFilters({
                                ...pendingFilters,
                                status: newStatuses,
                              });
                            }}
                          />
                          <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {user.isSuperAdmin && (
                    <div className="filter-group">
                      <label className="filter-label">Financial Institution</label>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={pendingFilters.fiId || ''}
                          onChange={(e) =>
                            setPendingFilters({
                              ...pendingFilters,
                              fiId: e.target.value,
                              merchantId: '',
                            })
                          }
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>All</em>
                          </MenuItem>
                          {(financialInstitutions?.items || []).map((fi) => (
                            <MenuItem key={fi.id} value={String(fi.id)}>
                              {fi.businessName}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  )}

                  {(user.isSuperAdmin || user.isBankAdmin || user.isPrimaryBankAdmin) && (
                    <div className="filter-group">
                      <label className="filter-label">Merchant</label>
                      <FormControl size="small" fullWidth>
                        <Select
                          value={pendingFilters.merchantId || ''}
                          onChange={(e) =>
                            setPendingFilters({
                              ...pendingFilters,
                              merchantId: e.target.value,
                            })
                          }
                          displayEmpty
                        >
                          <MenuItem value="">
                            <em>All</em>
                          </MenuItem>
                          {merchantAccounts.map((merchant) => (
                            <MenuItem key={merchant.terra_account_id} value={String(merchant.terra_account_id)}>
                              {merchant?.business_name || merchant?.account_name || merchant?.name || `Merchant ${merchant.terra_account_id}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  )}
                </div>

                <div className="filters-actions">
                  <button
                    type="button"
                    className="btn-apply-filters"
                    onClick={() => {
                      handleApplyFilters(pendingFilters);
                      setShowFilters(false);
                    }}
                  >
                    Apply Filters
                  </button>
                  <button
                    type="button"
                    className="btn-reset-filters"
                    onClick={handleClearAllFilters}
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            )}

            <TransactionsTab
                data={listData}
                loading={listLoading}
                error={listError}
                onPageChange={handlePageChange}
                currentPage={currentPage}
                statusFilter={statusFilter}
                onSearch={handleSearch}
                token={token}
                user={user}
            />
          </div>
        </Column>
      </Columns>
    </div>
  );
};

TransactionsListPage.propTypes = {
  setBreadcrumbs: propTypes.func.isRequired,
  token: propTypes.object,
  user: propTypes.object,
  financialInstitutions: propTypes.object,
  fetchFIs: propTypes.func.isRequired,
  summaryData: propTypes.object,
  summaryLoading: propTypes.bool,
  summaryError: propTypes.string,
  listData: propTypes.object,
  listLoading: propTypes.bool,
  listError: propTypes.string,
  fetchTransactionSummary: propTypes.func.isRequired,
  fetchTransactionList: propTypes.func.isRequired,
  fetchFITransactionSummary: propTypes.func.isRequired,
  fetchFITransactionList: propTypes.func.isRequired,
  fetchAllTransactionsSummary: propTypes.func.isRequired,
  fetchAllTransactionsList: propTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    token: getToken(state),
    user: getUser(state),
    financialInstitutions: getFIs(state),
    summaryData: state.transaction?.summary?.data,
    summaryLoading: state.transaction?.summary?.loading,
    summaryError: state.transaction?.summary?.error,
    listData: state.transaction?.list?.data,
    listLoading: state.transaction?.list?.loading,
    listError: state.transaction?.list?.error,
  };
}

const connectedTransactionsListPage = connect(mapStateToProps, {
  setBreadcrumbs,
  fetchFIs,
  fetchTransactionSummary,
  fetchTransactionList,
  fetchFITransactionSummary,
  fetchFITransactionList,
  fetchAllTransactionsSummary,
  fetchAllTransactionsList,
})(TransactionsListPage);

export { connectedTransactionsListPage as TransactionsListPage };
